import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreTradeRoute } from '../../../containers/Routes/routes';
import { useAppStateDispatch, useAppStateSelector } from '../../../providers/AppStateProvider';
import type { AppState } from '../../../providers/AppStateProvider/types';
import type { OrderService } from '../../OMS/NewOrder/OrderService';
import { selectUseTradeAllocations } from '../../OMS/NewOrder/OrderSlice';
import { type PreTradeRequest, useRequestPreTradeMutation } from '../redux/preTradeApi';
import { clearUserSelectedReference, setActiveSession } from '../redux/preTradeSlice';
import { PreTradeTabType } from '../types';
import { usePreTradeSessionConfirmation } from './usePreTradeSessionConfirmation';

/**
 * Hook that handles opening the Pre-Trade panel.
 * Uses the same OrderService request-construction path as actual order submission.
 */
export function useOpenPreTradePanel(orderService: OrderService) {
  const dispatch = useAppStateDispatch();
  const navigate = useNavigate();
  const [requestPreTrade, { isLoading: isPreTradeLoading }] = useRequestPreTradeMutation();
  const { resolveSessionID: resolveSession, ConfirmationDialog } = usePreTradeSessionConfirmation();

  const layoutId = useAppStateSelector((state: AppState) => state.appLayout.currentLayout?.id);
  const useTradeAllocations = useAppStateSelector(selectUseTradeAllocations);

  const openPreTradePanel = useCallback(async () => {
    const orderRequest = orderService.getOrderRequestForPreTrade(useTradeAllocations);

    if (!orderRequest) {
      return;
    }

    const { sessionId, action } = await resolveSession(orderRequest.Symbol);
    if (action === 'cancel') {
      return;
    }

    const request: PreTradeRequest = {
      SessionID: sessionId,
      Order: orderRequest,
    };

    // Clear the user-selected lock so the incoming API response can update the reference.
    dispatch(clearUserSelectedReference());
    const { error } = await requestPreTrade(request);
    if (error) {
      return;
    }

    dispatch(setActiveSession({ sessionId }));

    if (layoutId) {
      navigate(getPreTradeRoute({ layoutId, sessionId, tab: PreTradeTabType.Scenarios }));
    }
  }, [dispatch, layoutId, navigate, orderService, requestPreTrade, resolveSession, useTradeAllocations]);

  return { openPreTradePanel, isPreTradeLoading, ConfirmationDialog };
}
